/**
 * Field-permission catalog — the finer per-field layer of the data-visibility axis
 * (Berechtigungskonzept V3.0 §5.2 "Feldrechte", WS1 Bereich-3 delta).
 *
 * WHAT THIS AXIS IS (complements — does NOT duplicate — the Story V10-E deny-list):
 *   - The `role_visibility_deny` deny-list (visibility.ts) can only HIDE a Kachel /
 *     Tab / Menüpunkt. It has NO editable/read-only dimension.
 *   - Feldrechte add exactly that missing bit: per (role, entity, field) a
 *     `(visible, editable)` pair. UC6 Courtage-Satz: some roles see-and-edit, some
 *     see-read-only, some don't see it at all.
 *   - Default (no row) = visible + editable — Valerio's "Minus-Basis, nicht Plus-Basis":
 *     ~100 fields open, ~2 restricted (§3.9).
 *   - Resolution across a user's roles MIRRORS the deny-list exactly (subtractive /
 *     union-of-visibility): a field is hidden/read-only only if EVERY role the user
 *     holds restricts it; one unrestricted role unlocks it (see the resolver — the
 *     intersection of per-role restriction sets, matching role_visibility_deny).
 *   - EXCEPTION — `opt_in` fields (besondere Personendaten, Ausschreibung Ch 2.4 / 9):
 *     the polarity is INVERTED to secure-by-default. Default (no row) = HIDDEN + NOT
 *     editable for EVERYONE (wildcard admin + claim fallback included: "Admin-Sein allein
 *     genuegt nie"). An explicit row with `visible=true` is the GRANT; `editable=true` on
 *     that row additionally grants the write. Grants are UNIONED across the caller's roles
 *     (one granting role unlocks, the same least-restrictive direction as above). The
 *     restriction sets are irrelevant for an opt-in key: only the grant sets decide.
 *
 * DESIGN DECISIONS (the Epic-12 catalog-as-code pattern, mirroring visibility.ts):
 *   1. CATALOG = CODE, NOT A TABLE. The DB (`field_permissions`, migration 0109)
 *      stores only the per-role restriction rows. Adding a field needs no migration.
 *   2. NO dot-path hierarchy (unlike visibility): a field is a leaf. Resolution is an
 *      EXACT composite-key match (`entity_type::field_key`).
 *   3. `as const satisfies …` keeps every entry's literals narrow (PermissionKey pattern).
 *   4. Keys are lowercase ASCII; labels carry the real German domain terms.
 *
 * The full sensitive-field list is Stefan-gated (§6 action item "critical-field list");
 * this seed wires the 2-3 known examples (Courtage-Satz / Prämie / Risiko) — config-driven,
 * so the rest land with no code change.
 *
 * [Source: meetings/2026-08-24_ANALYSIS_berechtigungskonzept.md §2.4 (5.2) + §2.9 UC6]
 * [Source: packages/db/migrations/0109_field_permissions.sql]
 * [Source: packages/shared/src/visibility.ts (catalog-as-code + resolution-predicate precedent)]
 */

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * Shape of a single field-permission catalog entry.
 *
 * `entity_type` — the domain object the field lives on (e.g. 'policy', 'customer').
 * `field_key`   — the field within that entity (e.g. 'courtage_satz', 'praemie').
 * `label`       — user-visible label for the config UI (German domain terms).
 * `description` — one-line helper text (what the field carries).
 * `sensitive`   — informational flag for the config UI (highlighting); no enforcement.
 * `wire_ready`  - POLARITY: true = wire-ready but NOT yet backed by data, so the config
 *                 API stops OFFERING it as a new rule (field-permission.service.ts). The
 *                 entry STAYS in the catalog (key validation, resolver, stored rules keep
 *                 working). Drop the flag when the backing column lands.
 * `opt_in`      - POLARITY INVERTED (secure-by-default): true = the field is HIDDEN + NOT
 *                 editable unless a role carries an explicit row with visible=true (the
 *                 GRANT). The config API keeps such a row (it is NOT an all-default no-op)
 *                 and OFFERS the field with the flag on the wire; the role wizard renders it
 *                 default-denied (untouched = no grant) and only sends visible=true on an
 *                 explicit toggle. Enforced by isFieldHidden / isFieldReadOnly via the
 *                 resolver's grant sets.
 */
export interface FieldPermissionKeyDefinition {
  entity_type: string;
  field_key: string;
  label: string;
  description: string;
  sensitive?: boolean;
  wire_ready?: boolean;
  opt_in?: boolean;
}

// ── Field-permission catalog (the seed — WS1 §5.2) ────────────────────────────

/**
 * The static field-permission catalog. Seeds the 2-3 known sensitive fields as
 * examples (§3.9 "~2 restricted"); the full list is Stefan-gated → config-driven.
 *
 *   policy.praemie      ⭐ — enforced on the policy premium write path (WS1).
 *   policy.courtage_satz ⭐ — catalog-only for now (no courtage column on policy_version yet;
 *                             joins enforcement when that column lands — the policen.risiko
 *                             precedent in visibility.ts).
 *   customer.risiko     ⭐ — catalog-only for now (risk module not built).
 *
 * The two catalog-only entries carry `wire_ready: true`: they stay in the catalog (key
 * validation, resolver, existing role rules) but the config API no longer offers them as
 * new rules, because no column feeds them yet. Drop the flag when the column lands.
 */
export const FIELD_PERMISSION_CATALOG = [
  // ⭐ Prämie & Abrechnung — premium_net/gross, Stempelsteuer, fees (policy_version write).
  {
    entity_type: 'policy',
    field_key: 'praemie',
    label: 'Police: Prämie & Abrechnung',
    description: 'Prämie (netto/brutto), Stempelsteuer und Gebühren einer Police-Version.',
    sensitive: true,
  },
  // ⭐ Courtage-/Provisionssatz (UC6) — catalog-only until the courtage column lands.
  {
    entity_type: 'policy',
    field_key: 'courtage_satz',
    label: 'Police: Courtage-Satz',
    description: 'Provisions-/Courtagesatz einer Police (Finance).',
    sensitive: true,
    wire_ready: true,
  },
  // ⭐ Risikostufe / Bonität (§5.2) — catalog-only until the risk module lands.
  {
    entity_type: 'customer',
    field_key: 'risiko',
    label: 'Kunde: Risikostufe',
    description: 'Risikoeinstufung / Bonität des Kunden.',
    sensitive: true,
    wire_ready: true,
  },
  // ⭐ P2 Placement Slice 1 — the two besonders schützenswerten Kunde-Risikoprofil-Felder
  // (Datenschutz-Gating, Ausschreibung Ch 2.4 / 9). Backed by real columns on
  // customer_risk_profile (migration 0142), so NO wire_ready flag; the api field-gate enforces
  // (visible/editable) on the read + write of those two columns. `opt_in: true` INVERTS the
  // default (N3, secure-by-default): HIDDEN for every caller, admin included, until a role is
  // explicitly granted (field_permissions row with visible=true). The config API offers them
  // flagged `opt_in`; the role wizard renders them default-denied and sends the grant only on an
  // explicit toggle (a hand-made PUT works the same way).
  // Distinct from `customer::risiko` (Risikostufe/Bonität — a different concept).
  {
    entity_type: 'customer',
    field_key: 'gesundheitspruefung_status',
    label: 'Kunde: Statusflag Gesundheitsprüfung',
    description: 'Statusflag der Gesundheitsprüfung (ausstehend/abgeschlossen) im Risikoprofil; besondere Personendaten.',
    sensitive: true,
    opt_in: true,
  },
  {
    entity_type: 'customer',
    field_key: 'einkommens_bandbreite',
    label: 'Kunde: Einkommens-Bandbreite',
    description: 'Einkommens-Bandbreite der Privatperson im Risikoprofil; besondere Personendaten.',
    sensitive: true,
    opt_in: true,
  },
] as const satisfies readonly FieldPermissionKeyDefinition[];

// ── Derived types + helpers ───────────────────────────────────────────────────

/** Union of every catalog entity_type. */
export type FieldEntityType = (typeof FIELD_PERMISSION_CATALOG)[number]['entity_type'];

/**
 * Composite-key separator. A field restriction is keyed `entity_type::field_key`
 * everywhere it flows as a flat string (resolver sets, wire arrays) — the exact
 * analogue of a visibility dot-path key, but with NO hierarchy semantics.
 */
export const FIELD_PERMISSION_SEPARATOR = '::' as const;

/** Build the flat composite key for one (entity, field). */
export function fieldCompositeKey(entityType: string, fieldKey: string): string {
  return `${entityType}${FIELD_PERMISSION_SEPARATOR}${fieldKey}`;
}

/** Flat readonly array of every catalog composite key (validation + UI). */
export const FIELD_PERMISSION_COMPOSITE_KEYS: readonly string[] = FIELD_PERMISSION_CATALOG.map((f) =>
  fieldCompositeKey(f.entity_type, f.field_key),
);

/** Is (entity, field) a known catalog field? (config-API key validation). */
export function isKnownFieldPermission(entityType: string, fieldKey: string): boolean {
  return FIELD_PERMISSION_CATALOG.some(
    (f) => f.entity_type === entityType && f.field_key === fieldKey,
  );
}

// ── Opt-in (default-deny) fields — the inverted-polarity subset ───────────────

// Widened view: the `as const satisfies` element union lacks the optional flags (TS2339 on
// a direct `.opt_in` read) — the same widening the api services use for `wire_ready`.
const CATALOG_DEFINITIONS: readonly FieldPermissionKeyDefinition[] = FIELD_PERMISSION_CATALOG;

/**
 * The composite keys whose DEFAULT is hidden + non-editable (catalog `opt_in: true`).
 * Derived from the catalog so the catalog stays the single source of truth; today the two
 * besonders schuetzenswerten Kunde-Risikoprofil-Felder.
 */
export const FIELD_PERMISSION_OPT_IN_KEYS: ReadonlySet<string> = new Set(
  CATALOG_DEFINITIONS.filter((f) => f.opt_in === true).map((f) =>
    fieldCompositeKey(f.entity_type, f.field_key),
  ),
);

/** Is (entity, field) an opt-in field (default deny, allow only on an explicit grant)? */
export function isFieldOptIn(entityType: string, fieldKey: string): boolean {
  return FIELD_PERMISSION_OPT_IN_KEYS.has(fieldCompositeKey(entityType, fieldKey));
}

/**
 * Is a (visible, editable) rule the field's DEFAULT, i.e. a no-op row the config API drops
 * (the Minus-Basis normalisation — the table only holds rules that change something)?
 *   - regular field: default = visible + editable → `visible && editable` is the no-op.
 *   - opt-in field:  default = hidden (+ non-editable) → `!visible` is the no-op (a hidden
 *     field is never editable, so `visible=false, editable=true` collapses to the default
 *     too). A `visible=true` row is the GRANT and must be KEPT.
 */
export function isDefaultFieldRule(
  entityType: string,
  fieldKey: string,
  visible: boolean,
  editable: boolean,
): boolean {
  return isFieldOptIn(entityType, fieldKey) ? !visible : visible && editable;
}

// ── Flagship named constants (no magic strings at call sites) ─────────────────

/** Entity keys. */
export const FIELD_ENTITY_POLICY = 'policy' as const;
export const FIELD_ENTITY_CUSTOMER = 'customer' as const;

/** The premium block on a policy version — the WS1 write-enforced example. */
export const FIELD_KEY_POLICY_PRAEMIE = 'praemie' as const;
/** The Courtage-/Provisionssatz on a policy (UC6). */
export const FIELD_KEY_POLICY_COURTAGE_SATZ = 'courtage_satz' as const;
/** The customer risk level / Bonität. */
export const FIELD_KEY_CUSTOMER_RISIKO = 'risiko' as const;
/** The Kunde-Risikoprofil Statusflag Gesundheitsprüfung (P2 Slice 1 — gated column). */
export const FIELD_KEY_CUSTOMER_GESUNDHEITSPRUEFUNG_STATUS = 'gesundheitspruefung_status' as const;
/** The Kunde-Risikoprofil Einkommens-Bandbreite (P2 Slice 1 — gated column). */
export const FIELD_KEY_CUSTOMER_EINKOMMENS_BANDBREITE = 'einkommens_bandbreite' as const;

// ── Resolution predicates (mirror isVisibilityDenied — EXACT match, no hierarchy) ──

/**
 * Is (entity, field) HIDDEN given an effective hidden-field set?
 *
 * Exact composite-key match (fields are leaves — no dot-path ancestry, unlike the
 * visibility axis). `undefined` / empty set → nothing hidden (fail-open: the field
 * layer is a subtractive shaping layer; the Epic-12 permission gates stay the hard
 * authorization line). Pure function.
 *
 * OPT-IN fields (FIELD_PERMISSION_OPT_IN_KEYS) invert this: they are hidden UNLESS the
 * composite key is in `grantedVisible` (the resolver's union of explicit visible=true rows
 * across the caller's roles). `undefined` / empty grant set → HIDDEN (fail-CLOSED — the
 * secure default is the whole point). The `hidden` set is not consulted for them.
 */
export function isFieldHidden(
  hidden: ReadonlySet<string> | undefined,
  entityType: string,
  fieldKey: string,
  grantedVisible?: ReadonlySet<string>,
): boolean {
  const composite = fieldCompositeKey(entityType, fieldKey);
  if (FIELD_PERMISSION_OPT_IN_KEYS.has(composite)) {
    return !(grantedVisible?.has(composite) ?? false);
  }
  if (hidden === undefined || hidden.size === 0) return false;
  return hidden.has(composite);
}

/**
 * Is (entity, field) READ-ONLY given an effective read-only-field set?
 * Same contract as isFieldHidden. A hidden field is implicitly non-editable too
 * (callers combine both sets — see the api field-permission gate).
 *
 * OPT-IN fields are read-only UNLESS the composite key is in `grantedEditable` (the
 * resolver's union of explicit visible=true AND editable=true rows). `undefined` / empty
 * grant set → READ-ONLY (fail-closed). The `readOnly` set is not consulted for them.
 */
export function isFieldReadOnly(
  readOnly: ReadonlySet<string> | undefined,
  entityType: string,
  fieldKey: string,
  grantedEditable?: ReadonlySet<string>,
): boolean {
  const composite = fieldCompositeKey(entityType, fieldKey);
  if (FIELD_PERMISSION_OPT_IN_KEYS.has(composite)) {
    return !(grantedEditable?.has(composite) ?? false);
  }
  if (readOnly === undefined || readOnly.size === 0) return false;
  return readOnly.has(composite);
}

// ── Wire types ────────────────────────────────────────────────────────────────

/**
 * One per-role field rule (config API). `visible`/`editable` default true; an
 * explicit row only RESTRICTS (the Minus-Basis) — except on an opt-in field, where the
 * default is hidden and an explicit `visible=true` row is the GRANT (isDefaultFieldRule).
 * Mirrors the `field_permissions` row shape (0109) minus tenant/role/timestamps.
 */
export interface FieldPermissionDto {
  entity_type: string;
  field_key: string;
  visible: boolean;
  editable: boolean;
}

/** Response body for GET /admin/roles/:roleId/field-permissions (config read). */
export interface RoleFieldPermissionsResponse {
  /** The role's explicit field restriction rows (unsorted-into-catalog-order by the service). */
  field_permissions: FieldPermissionDto[];
}

/** Request body for PUT /admin/roles/:roleId/field-permissions (replace the set). */
export interface SetRoleFieldPermissionsBody {
  field_permissions: FieldPermissionDto[];
}

/**
 * Response body for GET /me/effective-field-permissions (the caller's effective
 * restriction, resolved as the intersection across the caller's roles, plus every
 * opt-in field the caller has NOT been granted). Both are flat composite-key arrays
 * (`entity_type::field_key`), sorted ascending.
 */
export interface EffectiveFieldPermissionsResponse {
  /** Fields fully hidden for the caller (visible=false won across every role, or an ungranted opt-in field). */
  hidden_fields: string[];
  /** Fields visible-but-read-only for the caller (editable=false won across every role, or an opt-in field granted visible only). */
  read_only_fields: string[];
}
